// Review Dokumen (/review-dokumen, feature document_review, every user-app
// role; migration 0044). Pluto reviews a document the user uploads or pastes.
//   dr_header   plain spacing   h1 and what the page is for
//   dr_form     card            Unggah file or Tempel teks, Jenis dokumen,
//                               Judul, Project (optional, for consistency
//                               with its saved activity documents), Arahan
//   dr_result   card            Hasil reviu (../docreview/ReviewResult.tsx),
//                               opened from the form or the history (?id=)
//   dr_history  card            Riwayat reviu: Buka, Unduh hasil reviu, Hapus
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import * as API from "../api";
import type { DocReview, DocReviewSummary, DocReviewType, Project } from "../api";
import { DOC_TYPE_LABEL, DOC_TYPES, MAX_INSTRUCTIONS, MAX_TEXT, MIN_TEXT, STATUS_LABEL, STATUS_TONE, UPLOAD_ACCEPT, reviewErrorMessage, reviewErrorTitle, uploadProblem } from "../docreview/labels";
import { ReviewResult } from "../docreview/ReviewResult";
import { formatCount, formatDate } from "../lib/format";
import { saveFile } from "../pbj/docx";
import { setQuery, useLocation } from "../router";
import { useFeatures } from "../shell/features";
import { Menu } from "../ui/Menu";
import { useElapsed } from "../ui/generation";
import { Badge, Dialog, EmptyState, Field, Notice, PageHeader, SkeletonRows, fieldAria, useAsync, useTitle } from "../ui/components";
import { FilePickField } from "../ui/FilePickField";
import { Glyph } from "../ui/icons";

type Source = "file" | "text";

const SOURCES: { id: Source; label: string }[] = [
  { id: "file", label: "Unggah file" },
  { id: "text", label: "Tempel teks" },
];

function ReviewForm({ projects, onReviewed }: { projects: Project[]; onReviewed: (review: DocReview) => void }) {
  const picker = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<Source>("file");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [textError, setTextError] = useState<string | null>(null);
  const [docType, setDocType] = useState<DocReviewType | "">("");
  const [typeError, setTypeError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState("");
  const [instructions, setInstructions] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const seconds = useElapsed(running);

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.currentTarget.files?.[0] ?? null;
    setError(null);
    if (!next) return;
    const problem = uploadProblem(next);
    setFile(problem ? null : next);
    setFileError(problem);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (running) return;
    let ok = true;
    if (!docType) {
      setTypeError("Pilih jenis dokumen.");
      ok = false;
    }
    if (source === "file" && !file) {
      setFileError("Pilih file yang akan direviu.");
      ok = false;
    }
    if (source === "text" && text.trim().length < MIN_TEXT) {
      setTextError(`Tempel teks dokumen, minimal ${MIN_TEXT} karakter.`);
      ok = false;
    }
    if (!ok || !docType) return;
    setRunning(true);
    setError(null);
    try {
      const review = await API.createDocReview({
        docType,
        file: source === "file" ? file : null,
        text: source === "text" ? text : undefined,
        title: title.trim() || undefined,
        projectId: projectId || null,
        instructions: instructions.trim() || undefined,
      });
      onReviewed(review);
      setFile(null);
      setText("");
      setTitle("");
      if (picker.current) picker.current.value = "";
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="card" aria-labelledby="dr-form-heading">
      <div className="section-head">
        <div className="section-head-text">
          <h2 id="dr-form-heading" className="card-title">
            Review dokumen baru
          </h2>
          <p className="section-lead">Pluto memeriksa kelengkapan, konsistensi, dan kesesuaian dokumen dengan ketentuan pengadaan barang/jasa pemerintah.</p>
        </div>
      </div>
      <form className="form-stack" noValidate onSubmit={submit} aria-label="Review dokumen baru" aria-busy={running || undefined}>
        {/* The input mode is a segmented choice, not tabs (Fase 4: Tabs only switch content views). */}
        <fieldset className="ui-segmented dr-source">
          <legend>Sumber dokumen</legend>
          {SOURCES.map((o) => (
            <label key={o.id}>
              <input type="radio" name="dr-source" value={o.id} checked={source === o.id} disabled={running} onChange={() => setSource(o.id)} />
              <span>{o.label}</span>
            </label>
          ))}
        </fieldset>
        <div id="dr-source-panel">
          {source === "file" ? (
            <FilePickField
              id="dr"
              required
              accept={UPLOAD_ACCEPT}
              file={file}
              onPick={onPick}
              inputRef={picker}
              disabled={running}
              error={fileError}
              hint="Word (.docx), PDF, Excel (.xlsx), atau teks (.txt), maksimal 10 MB. PDF hasil pindai belum dapat dibaca."
            />
          ) : (
            <Field id="dr-text" label="Teks dokumen" required error={textError} hint={`${formatCount(text.length)} karakter`}>
              <textarea
                {...fieldAria("dr-text", textError)}
                className="dr-textarea"
                rows={8}
                maxLength={MAX_TEXT}
                value={text}
                onChange={(e) => {
                  setText(e.currentTarget.value);
                  setTextError(null);
                }}
                placeholder="Tempel isi dokumen di sini."
              />
            </Field>
          )}
        </div>
        <div className="field-row">
          <Field id="dr-type" label="Jenis dokumen" required error={typeError}>
            <select
              {...fieldAria("dr-type", typeError)}
              value={docType}
              onChange={(e) => {
                setDocType(e.currentTarget.value as DocReviewType | "");
                setTypeError(null);
              }}
            >
              <option value="">Pilih jenis dokumen</option>
              {DOC_TYPES.map((t) => (
                <option key={t} value={t}>
                  {DOC_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field id="dr-project" label="Project terkait" hint="Opsional. Pluto membandingkan dokumen dengan dokumen aktivitas yang tersimpan di project ini.">
            <select {...fieldAria("dr-project")} value={projectId} onChange={(e) => setProjectId(e.currentTarget.value)} disabled={projects.length === 0}>
              <option value="">{projects.length === 0 ? "Belum ada project" : "Tanpa project"}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field id="dr-title" label="Judul" hint={source === "file" ? "Opsional. Tanpa judul, nama file yang dipakai." : "Opsional. Tanpa judul, jenis dokumen yang dipakai."}>
          <input {...fieldAria("dr-title")} value={title} maxLength={300} onChange={(e) => setTitle(e.currentTarget.value)} placeholder="Contoh: KAK Pengadaan Laptop 2027" />
        </Field>
        <Field id="dr-instructions" label="Arahan review" hint={`Opsional. Contoh: fokus pada spesifikasi teknis dan perhitungan harga. Maksimal ${formatCount(MAX_INSTRUCTIONS)} karakter.`}>
          <textarea {...fieldAria("dr-instructions")} rows={3} maxLength={MAX_INSTRUCTIONS} value={instructions} onChange={(e) => setInstructions(e.currentTarget.value)} />
        </Field>
        {error ? (
          <Notice tone={error instanceof API.ApiError && error.status === 429 ? "warning" : "danger"} title={reviewErrorTitle(error)}>
            <p>{reviewErrorMessage(error)}</p>
          </Notice>
        ) : null}
        {running ? (
          <div className="gen-progress" role="status" aria-live="polite">
            <p className="gen-title">Pluto mereviu dokumen</p>
            <div className="ui-progress">
              <progress aria-label="Pluto mereviu dokumen" />
            </div>
            <p className="gen-meta">
              <span className="num">{seconds}</span> detik berjalan. {seconds >= 45 ? "Masih diproses, mohon tunggu sebentar lagi." : "Biasanya 15 sampai 60 detik. Jangan tutup halaman ini."}
            </p>
          </div>
        ) : null}
        <div className="button-row">
          <button type="submit" className="btn" aria-busy={running || undefined} disabled={running}>
            <Glyph name="shieldTick" />
            <span>{running ? "Pluto mereview" : "Review dokumen"}</span>
          </button>
        </div>
      </form>
    </section>
  );
}

function metaOf(r: DocReviewSummary): string {
  return [formatDate(r.createdAt), r.projectName ? `Project ${r.projectName}` : null, r.findingsCount === 0 ? "Tidak ada temuan" : `${formatCount(r.findingsCount)} temuan`]
    .filter(Boolean)
    .join(" · ");
}

export function ReviewDokumenScreen() {
  useTitle("Review Dokumen");
  const features = useFeatures();
  const projectsOn = features.list !== null && features.has("projects");
  const history = useAsync<DocReviewSummary[]>(() => API.listDocReviews(), []);
  const projects = useAsync<Project[]>(() => (projectsOn ? API.listProjects() : Promise.resolve([])), [projectsOn]);
  const location = useLocation();
  const openId = location.query.get("id");
  const [open, setOpen] = useState<DocReview | null>(null);
  const [openError, setOpenError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [deleting, setDeleting] = useState<DocReviewSummary | DocReview | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const show = (id: string | null) => setQuery({ id: id ?? undefined });

  // The review in ?id=: from the last answer when it is that one, read otherwise.
  useEffect(() => {
    setOpenError(null);
    if (!openId) {
      setOpen(null);
      return;
    }
    if (open?.id === openId) return;
    let alive = true;
    API.getDocReview(openId).then(
      (r) => alive && setOpen(r),
      (err) => {
        if (!alive) return;
        setOpen(null);
        setOpenError(err);
      },
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  // A newly opened result scrolls into view (the form sits above it).
  useEffect(() => {
    if (open) resultRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [open?.id]);

  const onReviewed = (review: DocReview) => {
    setOpen(review);
    show(review.id);
    history.reload();
  };

  const download = async (id: string) => {
    if (busy) return;
    setBusy(id);
    setRowError(null);
    try {
      saveFile(await API.downloadDocReview(id));
    } catch (err) {
      setRowError({ id, message: reviewErrorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await API.deleteDocReview(deleting.id);
      if (open?.id === deleting.id) show(null);
      setDeleting(null);
      history.reload();
    } catch (err) {
      setDeleteError(reviewErrorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const list = history.data ?? [];

  return (
    <div className="region">
      <PageHeader
        divided
        title="Review Dokumen"
        lead="Unggah atau tempel dokumen pengadaan, pilih jenisnya, dan Pluto memeriksa kelengkapan, konsistensi, dan kesesuaiannya dengan aturan. Hasilnya dapat diunduh sebagai Word."
      />
      <ReviewForm projects={projects.data ?? []} onReviewed={onReviewed} />

      {open || openId ? (
        <div ref={resultRef} className="dr-result-anchor">
          {open ? (
            <ReviewResult
              review={open}
              busy={busy !== null || deleteBusy}
              onDownload={() => void download(open.id)}
              onDelete={() => {
                setDeleteError(null);
                setDeleting(open);
              }}
              onClose={() => show(null)}
            />
          ) : openError ? (
            <Notice tone="danger" title="Hasil review belum dapat dibuka" action={<button type="button" className="btn btn-secondary" onClick={() => show(null)}>Tutup</button>}>
              <p>{reviewErrorMessage(openError)}</p>
            </Notice>
          ) : openId ? (
            <SkeletonRows count={3} label="Memuat hasil review" />
          ) : null}
          {open && rowError?.id === open.id ? (
            <Notice tone="danger" title="Hasil review belum diunduh">
              <p>{rowError.message}</p>
            </Notice>
          ) : null}
        </div>
      ) : null}

      <section className="card" aria-labelledby="dr-history-heading">
        <div className="section-head">
          <div className="section-head-text">
            <h2 id="dr-history-heading" className="card-title">
              Riwayat review
            </h2>
            <p className="section-lead">Hanya Anda yang dapat melihat review ini.</p>
          </div>
        </div>
        {history.loading && !history.data ? (
          <SkeletonRows count={3} label="Memuat riwayat review" />
        ) : history.error && !history.data ? (
          <Notice
            tone="danger"
            title="Riwayat review belum dapat dimuat"
            action={
              <button type="button" className="btn btn-secondary" onClick={history.reload}>
                Muat ulang
              </button>
            }
          >
            <p>{reviewErrorMessage(history.error)}</p>
          </Notice>
        ) : list.length === 0 ? (
          <EmptyState icon="shieldTick" title="Belum ada dokumen yang direviu" body="Hasil setiap review tersimpan di sini dan dapat dibuka atau diunduh kembali." />
        ) : (
          <>
            <p className="form-note">{formatCount(list.length)} review</p>
            <ul className="rows" aria-label="Riwayat review">
              {list.map((r) => (
                <li key={r.id} className="row" data-current={r.id === openId || undefined}>
                  <span className="row-main">
                    <span className="row-title">{r.title}</span>
                    <span className="row-meta">
                      <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge> <Badge>{DOC_TYPE_LABEL[r.docType]}</Badge>
                    </span>
                    <span className="row-meta">{metaOf(r)}</span>
                    {rowError?.id === r.id && r.id !== open?.id ? (
                      <span className="pk-item-error" role="alert">
                        <Glyph name="danger" size={16} />
                        <span>{rowError.message}</span>
                      </span>
                    ) : null}
                  </span>
                  <span className="row-side">
                    <button type="button" className="btn btn-secondary" aria-label={`Buka hasil review ${r.title}`} aria-current={r.id === openId || undefined} onClick={() => show(r.id)}>
                      <Glyph name="eye" />
                      <span>Buka</span>
                    </button>
                    <Menu
                      label={`Aksi untuk ${r.title}`}
                      items={[
                        { id: "download", label: "Unduh hasil review", icon: "documentDownload", onSelect: () => void download(r.id) },
                        {
                          id: "delete",
                          label: "Hapus",
                          icon: "trash",
                          tone: "danger",
                          onSelect: () => {
                            setDeleteError(null);
                            setDeleting(r);
                          },
                        },
                      ]}
                    />
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <Dialog
        open={deleting !== null}
        title="Hapus hasil review?"
        description={deleting ? `Hasil review ${deleting.title} dihapus dari riwayat. Tindakan ini tidak dapat dibatalkan.` : undefined}
        onClose={() => setDeleting(null)}
        busy={deleteBusy}
        size="sm"
        footer={
          <>
            <button type="button" className="btn btn-secondary" disabled={deleteBusy} onClick={() => setDeleting(null)}>
              Batal
            </button>
            <button type="button" className="btn" data-tone="danger" aria-busy={deleteBusy || undefined} disabled={deleteBusy} onClick={() => void remove()}>
              Hapus hasil review
            </button>
          </>
        }
      >
        {deleteError ? <Notice tone="danger" title="Hasil review belum dihapus">{deleteError}</Notice> : null}
      </Dialog>
    </div>
  );
}
